
import { useContext, useEffect, useRef } from "react";
import { initialize, highlight, initIdentify, setCursor, closePopup } from "../../helpers/map/map";
import { mapStore } from "../../stores/mapStore";
import { WebMapProps } from "./types/webMapProps";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import '@arcgis/core/assets/esri/themes/light/main.css';
import './webMap.scss';
import PermissionsContext from "../../contexts/permissionsContext";

const WebMap = (props: WebMapProps) => {
    const elemRef = useRef<HTMLDivElement>(null);
    const { functionsPermissions } = useContext(PermissionsContext);

    useEffect(() => {
        if (!elemRef.current) return;
        let handlerClick: any = null;
        const _view = initialize(elemRef.current, props.config, props.baseMap);
        mapStore.setView(_view);

        const handlePopupAction = reactiveUtils.on(
            () => _view.popup,
            "trigger-action",
            (event) => {
                if (event.action.id === "select-popup-feature") {
                    _view.graphics.removeAll();
                    highlight(_view, _view.popup.selectedFeature);
                    mapStore.setSelected(_view.popup.selectedFeature);
                    _view.closePopup();
                }
            }
        );

        const handlePopupFeatureChange = reactiveUtils.watch(
            () => _view.popup.selectedFeature,
            (feature) => {
                _view.graphics.removeAll();
                if (feature) {
                    highlight(_view, feature, false);
                }
            });
        _view.addHandles(handlePopupAction);
        _view.addHandles(handlePopupFeatureChange);

        handlerClick = _view.on("click", function (event) {
            if (functionsPermissions.pointObjOnMap.active) {
                event.stopPropagation();
                _view.graphics.removeAll();
                setCursor(_view, true);
                closePopup(_view);

                const promiseArray = initIdentify(_view, props.config.services, event);
                if (promiseArray.length > 0) {
                    Promise.all(promiseArray).then((reslut) => {
                        const response = reslut.flat();
                        if (response.length > 1) {
                            mapStore.setSelected(null);
                            _view.openPopup({ features: response, location: event.mapPoint });
                        } else if (response.length === 1) {
                            highlight(_view, response[0]);
                            mapStore.setSelected(response[0]);
                        } else {
                            mapStore.setSelected(null);
                        }
                        setCursor(_view, false);
                    }, (err) => { setCursor(_view, false); console.log("Map error: ", err); });
                }
            }
        })

        return () => {
            _view?.destroy();
            handlerClick?.remove();
            mapStore.setSelected(null);
            mapStore.setView(null);
        };
    }, [props.baseMap, props.config, functionsPermissions.pointObjOnMap])

    return (
        <div className="mapDiv" ref={elemRef} />
    )
}

export default WebMap;